import React, { useState, useEffect } from 'react';
import { getTechPageData } from '../services/techService';
import { getStrapiMedia } from '../lib/strapi';


const Technology = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      const result = await getTechPageData();
      setData(result);
      setLoading(false);
    };
    fetchData();
  }, []);

  if (loading || !data) {
    return (
      <div className="py-40 text-center flex flex-col items-center justify-center space-y-4">
        <div className="w-12 h-12 border-4 border-[#50B17D] border-t-transparent rounded-full animate-spin"></div>
        <p className="text-gray-400 font-bold uppercase tracking-widest text-[10px]">Loading Technology...</p>
      </div>
    );
  }

  const heroImageUrl = getStrapiMedia(data.hero_Image);
  const wcLogoUrl = getStrapiMedia(data.widya_connect_logo);
  const wcMapUrl = getStrapiMedia(data.widya_connect_mapimage);



  // Split detail description into paragraphs
  const detailParagraphs = data.widya_connect_detaildes
    ? data.widya_connect_detaildes.split('\n\n').filter(p => p.trim() !== '')
    : [];

  return (
    <div className="bg-white min-h-screen">
      {/* Hero Section */}
      <section className="relative overflow-hidden h-[460px] min-h-[460px] flex items-center justify-center">
        {heroImageUrl ? (
          <>
            <div
              className="absolute inset-0 bg-cover bg-center bg-no-repeat"
              style={{ backgroundImage: `url(${heroImageUrl})` }}
            />
            <div className="absolute inset-0 bg-black/50" />
          </>
        ) : (
          <div className="absolute inset-0 bg-gray-50" />
        )}
        <div className="container mx-auto px-6 text-center relative z-10 w-full max-w-[1200px] py-8 md:py-12 flex flex-col items-center justify-center">
          <h1 className={`text-[24px] sm:text-[32px] md:text-[40px] lg:text-[46px] font-bold tracking-tight leading-[1.2] max-w-5xl mx-auto font-heading drop-shadow-lg ${heroImageUrl ? 'text-white' : 'text-widya-charcoal'}`}>
            {data.hero_title}
          </h1>
        </div>
      </section>

      {/* Section 1: WIDYAConnect Platform Overview */}
      <section className="py-20 md:py-28 px-6 bg-white border-b border-gray-100">
        <div className="container mx-auto max-w-[1200px]">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight tracking-tight text-widya-charcoal font-heading">
              {data.widya_connect_title || 'WIDYA Connect Platform Overview'}
            </h2>
            <div className="section-underline-center" />
          </div>

          <div className="flex justify-center max-w-5xl mx-auto">
            {wcLogoUrl ? (
              <div className="relative w-full p-4 bg-white rounded-3xl border border-gray-100 shadow-md overflow-hidden">
                <img
                  src={wcLogoUrl}
                  alt="WIDYA Connect Platform Diagram"
                  className="w-full h-auto object-contain"
                />
              </div>
            ) : (
              <div className="w-full h-[400px] bg-gray-50 rounded-3xl flex items-center justify-center text-gray-400 border border-dashed">
                No Connection Diagram Available
              </div>
            )}
          </div>

          {/* Section 1 Caption: Full Width Bottom Callout */}
          {data.widya_connect_description && (
            <div className="mt-20 text-center max-w-4xl mx-auto">
              <p className="text-[#333333] text-base md:text-lg lg:text-xl font-normal leading-relaxed">
                {data.widya_connect_description}
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Section 2: WIDYA Ecosystem and Coordination Network */}
      <section className="py-20 md:py-28 px-6 bg-gray-50/30">
        <div className="container mx-auto max-w-[1200px]">
          {data.widya_connect_subtitle && (
            <div className="text-center max-w-3xl mx-auto mb-16 animate-fade-in-up">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight tracking-tight text-widya-charcoal font-heading">
                {data.widya_connect_subtitle}
              </h2>
              <div className="section-underline-center" />
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            {/* Left Column: Flow Diagram */}
            <div className="lg:col-span-7 flex justify-center order-2 lg:order-1">
              {wcMapUrl ? (
                <div className="relative p-4 bg-white rounded-3xl border border-gray-100 shadow-md overflow-hidden w-full">
                  <img
                    src={wcMapUrl}
                    alt="WIDYA Ecosystem Flow Diagram"
                    className="w-full h-auto object-contain max-h-[500px]"
                  />
                </div>
              ) : (
                <div className="w-full h-[400px] bg-white rounded-3xl flex items-center justify-center text-gray-400 border border-dashed border-gray-200">
                  No Ecosystem Flow Diagram Available
                </div>
              )}
            </div>

            {/* Right Column: Detailed Flow Description */}
            <div className="lg:col-span-5 space-y-6 order-1 lg:order-2">
              <div className="space-y-6 lg:py-4">
                {detailParagraphs.map((para, idx) => (
                  <p 
                    key={idx} 
                    className="text-[#333333] text-base md:text-lg leading-relaxed font-normal"
                  >
                    {para}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Technology;